import { useCacheRefresh } from '../components/common/CacheRefreshProvider';
import { faRetweet, faCircleNotch, faArrowRight, faCheckCircle, faColumns, faGrip, faHouse, faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useRef, useState } from "react";
import PagesTree from "./contentmain/PagesTree";
import PageDetails from "./contentmain/PageDetails";
import { IActionResult, ILanguage, IVersionSection } from "../../models/Interfaces";
import styles from "./contentmain/contentHome.module.scss";
import { Button, Col, Form, FormControl, FormLabel, Modal, Row } from "react-bootstrap";
import { AddUpdatePageCommand } from "../../models/content/AddUpdatePageCommand";
import { ContentService } from "../../services/ContentService";
import { SiteService } from "../../services/SiteService";
import { SiteSwitcher } from "../../services/SiteSwitcher";
import { ContentEditorService } from "../../services/ContentEditorService";
import { TemplateType } from "../../config/Enums";
import { useNavigate } from "react-router-dom";

import { useConfirm } from "../../config/ConfirmProvider";

const SiteAdminHome = () => {
    const cacheRefresh = useCacheRefresh();
    const [loadingpages, setLoadingPages] = useState<Boolean>();
    const [selectedPage, setSelectedPage] = useState<number | null>();
    const [pageCount, setPageCount] = useState<number | null>(null);
    const [showFirstPageWizard, setShowFirstPageWizard] = useState<boolean>(false);
    const [wizardStep, setWizardStep] = useState<number>(1);
    const [creatingFirstPage, setCreatingFirstPage] = useState<boolean>(false);
    const [firstPageTitle, setFirstPageTitle] = useState<string>("Homepage");
    const [firstPageAlias, setFirstPageAlias] = useState<string>("Homepage");
    const [firstPageLanguage, setFirstPageLanguage] = useState<string>("");
    const [selectedWidth, setSelectedWidth] = useState<"centered" | "full">("centered");
    const [selectedSectionStyle, setSelectedSectionStyle] = useState<"content" | "columns">("content");
    const [selectedColumnCount, setSelectedColumnCount] = useState<number>(2);
    const [availableLanguages, setAvailableLanguages] = useState<Array<ILanguage>>([]);

    const treeRef = useRef<any>(null);
    const contentSvcRef = useRef<ContentService | null>(null);
    const contentEditSvcRef = useRef<ContentEditorService | null>(null);
    const siteSvcRef = useRef<SiteService | null>(null);
    const siteSwitchRef = useRef<SiteSwitcher | null>(null);
    const navigate = useNavigate();
    const { requestConfirmation } = useConfirm();
    const editStateRef = useRef({ dirty: false, busy: false });
    const selectionPromptRef = useRef(false);
    const selectedPageRef = useRef(selectedPage);
    selectedPageRef.current = selectedPage;
    const onEditStateChange = useCallback((dirty: boolean, busy: boolean) => { editStateRef.current = { dirty, busy }; }, []);
    const beforePageSelect = async (pageid: string): Promise<boolean> => {
      if (Number(pageid) === selectedPageRef.current) return true;
      if (editStateRef.current.busy || selectionPromptRef.current) return false;
      if (!editStateRef.current.dirty) return true;
      selectionPromptRef.current = true;
      try {
        const discard = await requestConfirmation("Discard your unsaved changes before selecting another page?", "Discard changes");
        if (discard) editStateRef.current = { dirty: false, busy: false };
        return discard;
      } finally { selectionPromptRef.current = false; }
    };
    
    const onPageSelected = (pageid: string | null) : void => {
        const pid = isNaN(Number(pageid)) ? null : Number(pageid);
        if (pageid) {
            setSelectedPage(pid);
        } else {
            setSelectedPage(null);
        }
    };

    const handlePageAction = (result: IActionResult) : void => {
      if (treeRef.current) {
        const pageid = isNaN(Number(result?.Data)) ? null : Number(result.Data);
        treeRef.current.getSitePages(pageid);
        setSelectedPage(pageid);
      }
    };

    const getFirstSectionTemplate = (): TemplateType => {
      if (selectedSectionStyle === "columns") {
        return selectedWidth === "full" ? TemplateType.ColumnsFW : TemplateType.ColumnsCC;
      }

      return selectedWidth === "full" ? TemplateType.ContentFW : TemplateType.ContentCC;
    };

    const createFirstPage = async (): Promise<void> => {
      if (!contentSvcRef.current || !contentEditSvcRef.current || !siteSwitchRef.current) {
        return;
      }

      setCreatingFirstPage(true);

      try {
        const pageCommand = new AddUpdatePageCommand();
        pageCommand.Language = firstPageLanguage;
        pageCommand.Title = firstPageTitle.trim();
        pageCommand.Alias = firstPageAlias.trim();
        pageCommand.Url = "/";
        pageCommand.Slug = "/";
        pageCommand.ParentPageId = null;
        pageCommand.ParentUrl = "";
        pageCommand.ExcludeSiteMap = false;
        pageCommand.IsNewsPage = false;

        const saveResult = await contentSvcRef.current.addUpdatePage(pageCommand);
        const pageId = Number(saveResult?.Data);

        if (!saveResult.Success || !pageId) {
          return;
        }

        const version = await contentSvcRef.current.addVersion(pageId);

        if (!version) {
          return;
        }

        const firstSection: IVersionSection = {
          BackgroundColour: undefined,
          BackgroundImage: undefined,
          ClientScript: undefined,
          Columns: selectedSectionStyle === "columns" ? selectedColumnCount : 12,
          ContentComponents: [],
          DynamicComponents: undefined,
          ExtendedProperties: undefined,
          Id: 0,
          IsPreview: false,
          JSFilePath: undefined,
          Name: "Homepage section",
          Order: 1,
          PageVersion: version,
          SectionClass: undefined,
          SectionHRefId: undefined,
          PageVersionId: version.Id,
          Template: getFirstSectionTemplate()
        };

        await contentEditSvcRef.current.SaveSection(firstSection);

        if (treeRef.current) {
          await treeRef.current.getSitePages(pageId);
          treeRef.current.selectPage(pageId.toString());
        }

        setSelectedPage(pageId);
        setShowFirstPageWizard(false);

        const siteId = siteSwitchRef.current.getSelectedSiteKey();
        if (siteId) {
          navigate(`/content/design/${siteId}/${version.Id}`);
        }
      } finally {
        setCreatingFirstPage(false);
      }
    };

    const openFirstPageWizard = (): void => {
      setWizardStep(1);
      setShowFirstPageWizard(true);
    };

    const canContinueToLayout = firstPageLanguage.trim().length > 0 && firstPageTitle.trim().length > 0 && firstPageAlias.trim().length > 0;

    useEffect(() => {
        setLoadingPages(false);
    }, []);

    useEffect(() => {
      if (!contentSvcRef.current) {
        contentSvcRef.current = new ContentService();
      }

      if (!contentEditSvcRef.current) {
        contentEditSvcRef.current = new ContentEditorService();
      }

      if (!siteSvcRef.current) {
        siteSvcRef.current = new SiteService();
      }

      if (!siteSwitchRef.current) {
        siteSwitchRef.current = new SiteSwitcher();
      }

      const getSiteDetails = async () => {
        const [details, languages] = await Promise.all([
          siteSvcRef.current?.getSiteDetails(),
          contentSvcRef.current?.getSiteLanguages()
        ]);

        if (languages?.length) {
          setAvailableLanguages(languages);
        }

        if (details?.DefaultLanguage) {
          setFirstPageLanguage(details.DefaultLanguage);
        }
      };

      getSiteDetails();
    }, []);

    useEffect(() => {
      if (pageCount === 0) {
        setShowFirstPageWizard(true);
      } else if ((pageCount ?? 0) > 0) {
        setShowFirstPageWizard(false);
      }
    }, [pageCount]);

    return (
      <>
        { loadingpages && (
          <div>
            <div className="row">
              <div className="col-md-12 text-center mt-5">
                <div>
                  <FontAwesomeIcon icon={faCircleNotch} size="4x" spin={true} className="grey-spinner"></FontAwesomeIcon>
                </div>
              </div>
              <div className="col-12 text-center mt-3">
                <div>
                  <label className="form-label">Loading pages...</label>
                </div>
              </div>
            </div>
          </div>            
        )}

        { !loadingpages && (
          <div className={styles.page}>
            <section className="app-list-hero">
              <div>
                <p className="app-list-hero__eyebrow">Content Management</p>
                <h1 className="app-list-hero__title">Pages and Versions</h1>
                <p className="app-list-hero__summary">
                Create new pages, organise site structure, and manage the content versions that power the live site.
                Select a page from the tree to edit its core details or start a new version for the designer.
                </p>
              </div>
              <div className="app-list-hero__actions">
                <Button type="button" variant="outline-primary" disabled={!cacheRefresh.canRefresh}
                  onClick={() => void cacheRefresh.refresh()} aria-describedby="page-cache-refresh-status">
                  <FontAwesomeIcon icon={faRetweet} spin={cacheRefresh.busy} className="me-2" />
                  Refresh published pages
                </Button>
                <div id="page-cache-refresh-status" className="small text-muted mt-2" role="status">{cacheRefresh.message}</div>
              </div>
            </section>
            <div className={styles.workspace}>
              <div className={styles.workspaceSidebar}>
                <PagesTree
                  beforePageSelect={beforePageSelect}
                  onPageSelected={onPageSelected}
                  onPageCountChange={setPageCount}
                  ref={treeRef}
                />
              </div>
              <div className={styles.workspaceMain}>
              
                {selectedPage && (
                  <PageDetails key={selectedPage} pageId={selectedPage} onEditStateChange={onEditStateChange} ActionCallback={handlePageAction} />
                )}

                {!selectedPage && (
                  <div className={styles.emptyPanel}>
                    <div className={styles.emptyState}>
                      {pageCount === 0 ? (
                        <>
                          <div className={styles.emptyStateIcon}>
                            <FontAwesomeIcon icon={faHouse} />
                          </div>
                          <span className={styles.emptyStateEyebrow}>First-Time Setup</span>
                          <h2 className={styles.emptyStateTitle}>Create your homepage</h2>
                          <p className={styles.emptyStateText}>
                            Start with a homepage, choose the first section style, and we’ll take you straight into the content editor.
                          </p>
                          <Button variant="primary" className={styles.onboardingPrimaryButton} onClick={openFirstPageWizard}>
                            <FontAwesomeIcon icon={faArrowRight} />
                            <span>Create your homepage</span>
                          </Button>
                        </>
                      ) : (
                        <>
                          <h2 className={styles.emptyStateTitle}>No page selected</h2>
                          <p className={styles.emptyStateText}>
                            Pick an existing page from the tree or create a new one to start managing content details and versions.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <Modal
          show={showFirstPageWizard && pageCount === 0}
          onHide={() => setShowFirstPageWizard(false)}
          size="lg"
          centered
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>Create your homepage</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className={styles.wizardShell}>
              {wizardStep === 1 && (
                <>
                  <div className={styles.wizardHeader}>
                    <span className={styles.wizardEyebrow}>Step 1 of 2</span>
                    <h2 className={styles.wizardTitle}>Set up the first page</h2>
                    <p className={styles.wizardText}>
                      We’ll create your homepage at <strong>/</strong> and use it as the starting point for your first content version.
                    </p>
                  </div>

                  <Row>
                    <Col md={6} className="mt-2">
                      <FormLabel>Site Language</FormLabel>
                      <Form.Control
                        as="select"
                        value={firstPageLanguage}
                        onChange={(e) => setFirstPageLanguage(e.target.value)}
                      >
                        <option value="">Select language...</option>
                        {availableLanguages.map((language) => (
                          <option key={language.Id} value={language.ShortCode}>{language.Language}</option>
                        ))}
                      </Form.Control>
                    </Col>
                    <Col md={6} className="mt-2">
                      <FormLabel>Page Title</FormLabel>
                      <FormControl
                        value={firstPageTitle}
                        onChange={(e) => setFirstPageTitle(e.target.value)}
                        placeholder="Homepage"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6} className="mt-3">
                      <FormLabel>Alias</FormLabel>
                      <FormControl
                        value={firstPageAlias}
                        onChange={(e) => setFirstPageAlias(e.target.value)}
                        placeholder="Homepage"
                      />
                    </Col>
                    <Col md={6} className="mt-3">
                      <FormLabel>Page Route</FormLabel>
                      <FormControl value="/" disabled />
                    </Col>
                  </Row>
                </>
              )}

              {wizardStep === 2 && (
                <>
                  <div className={styles.wizardHeader}>
                    <span className={styles.wizardEyebrow}>Step 2 of 2</span>
                    <h2 className={styles.wizardTitle}>Choose the first section style</h2>
                    <p className={styles.wizardText}>
                      Choose the layout for the first section of your homepage. You can always add more sections or change the structure later in the editor.
                    </p>
                  </div>

                  <div className={styles.wizardGrid}>
                    <div className={styles.wizardSection}>
                      <h3 className={styles.wizardSectionTitle}>Centered Content</h3>
                      <div className={styles.wizardCardGrid}>
                        <button
                          type="button"
                          className={`${styles.wizardChoiceCard} ${selectedWidth === "centered" && selectedSectionStyle === "content" ? styles.wizardChoiceCardActive : ""}`}
                          onClick={() => {
                            setSelectedWidth("centered");
                            setSelectedSectionStyle("content");
                          }}
                        >
                          <span className={styles.wizardChoiceIcon}>
                            <FontAwesomeIcon icon={faGrip} />
                          </span>
                          <span className={styles.wizardChoiceTitle}>Dynamic Content</span>
                          <span className={styles.wizardChoiceText}>Start with a drag-and-drop content area that sits neatly in the middle of the page.</span>
                          <span className={styles.wizardChoiceMeta}>
                            <FontAwesomeIcon icon={faCheckCircle} />
                            Recommended
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`${styles.wizardChoiceCard} ${selectedWidth === "centered" && selectedSectionStyle === "columns" ? styles.wizardChoiceCardActive : ""}`}
                          onClick={() => {
                            setSelectedWidth("centered");
                            setSelectedSectionStyle("columns");
                          }}
                        >
                          <span className={styles.wizardChoiceIcon}>
                            <FontAwesomeIcon icon={faColumns} />
                          </span>
                          <span className={styles.wizardChoiceTitle}>Columns Layout</span>
                          <span className={styles.wizardChoiceText}>Use a centred column-based section for a more structured first page layout.</span>
                        </button>
                      </div>
                    </div>

                    <div className={styles.wizardSection}>
                      <h3 className={styles.wizardSectionTitle}>Full Page Width Content</h3>
                      <div className={styles.wizardCardGrid}>
                        <button
                          type="button"
                          className={`${styles.wizardChoiceCard} ${selectedWidth === "full" && selectedSectionStyle === "content" ? styles.wizardChoiceCardActive : ""}`}
                          onClick={() => {
                            setSelectedWidth("full");
                            setSelectedSectionStyle("content");
                          }}
                        >
                          <span className={styles.wizardChoiceIcon}>
                            <FontAwesomeIcon icon={faGrip} />
                          </span>
                          <span className={styles.wizardChoiceTitle}>Dynamic Layout</span>
                          <span className={styles.wizardChoiceText}>Start with the drag-and-drop content area in a full page width section for flexible, freeform page building.</span>
                        </button>
                        <button
                          type="button"
                          className={`${styles.wizardChoiceCard} ${selectedWidth === "full" && selectedSectionStyle === "columns" ? styles.wizardChoiceCardActive : ""}`}
                          onClick={() => {
                            setSelectedWidth("full");
                            setSelectedSectionStyle("columns");
                          }}
                        >
                          <span className={styles.wizardChoiceIcon}>
                            <FontAwesomeIcon icon={faColumns} />
                          </span>
                          <span className={styles.wizardChoiceTitle}>Columns layout</span>
                          <span className={styles.wizardChoiceText}>Begin with a Bootstrap-style column layout for a more structured first section.</span>
                        </button>
                      </div>
                    </div>

                    {selectedSectionStyle === "columns" && (
                      <div className={styles.wizardSection}>
                        <h3 className={styles.wizardSectionTitle}>How many columns?</h3>
                        <Form.Control
                          as="select"
                          value={selectedColumnCount}
                          onChange={(e) => setSelectedColumnCount(Number(e.target.value))}
                        >
                          <option value={1}>1 column</option>
                          <option value={2}>2 columns</option>
                          <option value={3}>3 columns</option>
                          <option value={4}>4 columns</option>
                        </Form.Control>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className={styles.wizardActions}>
                <div className={styles.wizardActionGroup}>
                  {wizardStep === 2 && (
                    <Button variant="outline-secondary" onClick={() => setWizardStep(1)} disabled={creatingFirstPage}>
                      Back
                    </Button>
                  )}
                </div>
                <div className={styles.wizardActionGroup}>
                  <Button variant="outline-primary" onClick={() => setShowFirstPageWizard(false)} disabled={creatingFirstPage}>
                    Cancel
                  </Button>
                  {wizardStep === 1 && (
                    <Button variant="primary" onClick={() => setWizardStep(2)} disabled={!canContinueToLayout}>
                      Next
                    </Button>
                  )}
                  {wizardStep === 2 && (
                    <Button variant="success" onClick={createFirstPage} disabled={creatingFirstPage}>
                      {creatingFirstPage ? "Creating..." : "Create homepage"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </>
    );
}

export default SiteAdminHome;
